import { useQuery } from "@tanstack/react-query";
import { errorCode } from "./errors";
import { type RouterOutputs, useTRPC } from "./trpc";

/**
 * Accounts on a hosted server (BT_AUTH=accounts, ADR 0011). The desktop app and local development
 * have a single user until users are switched on (ADR 0013): then people pick themselves in the
 * user picker, with an optional PIN, and get the user menu, a profile and the welcome steps, but
 * no email, members or admin interface.
 */

export type AuthMode = RouterOutputs["system"]["info"]["auth"];
export type Me = NonNullable<RouterOutputs["account"]["me"]>;
export type PublicDemo = RouterOutputs["system"]["info"]["publicDemo"];

let mode: AuthMode = "none";
let demo: PublicDemo = null;
let localUsers = false;

/** Set once at start from system.info. */
export function setAuthMode(value: AuthMode, publicDemo: PublicDemo = null, usersOn = false): void {
  mode = value;
  demo = publicDemo;
  localUsers = usersOn;
}

/**
 * The public demo (ADR 0012): without a session, people see the page that sends them a demo
 * garage, not the sign-in form.
 */
export function publicDemo(): PublicDemo {
  return demo;
}

export function authMode(): AuthMode {
  return mode;
}

/** A hosted server's accounts: email, password, invitations, members, the admin interface. */
export function hasAccounts(): boolean {
  return mode === "accounts";
}

/** The desktop app with users switched on (ADR 0013). */
export function hasLocalUsers(): boolean {
  return localUsers;
}

/** Whether people sign in here: with accounts, or as the desktop app's users. */
export function signsIn(): boolean {
  return hasAccounts() || hasLocalUsers();
}

/** Who is signed in (in any status), or null. */
export function useMe() {
  const trpc = useTRPC();
  return useQuery({ ...trpc.account.me.queryOptions(), staleTime: 30_000 });
}

/**
 * The sign-in page, coming back to `next` (the current page) afterwards. In the public demo it is
 * the demo's request page, except for the admin interface.
 */
export function signInUrl(next = window.location.pathname + window.location.search): string {
  if (demo && !next.startsWith("/admin")) return "/account/demo";
  return next.startsWith("/account")
    ? "/account/sign-in"
    : `/account/sign-in?next=${encodeURIComponent(next)}`;
}

/**
 * Whether someone still has to go through the welcome steps of their first sign-in: language,
 * theme and, for a visitor of the public demo, their demo garage.
 */
export function needsWelcome(me: Me): boolean {
  return me.status === "active" && (!me.welcomed || (me.demo !== null && me.demo.garage === null));
}

/** The welcome steps, going on to `next` (the current page) afterwards. */
export function welcomeUrl(next = window.location.pathname + window.location.search): string {
  return `/account/welcome?next=${encodeURIComponent(next)}`;
}

/** A path from ?next= that stays within the app. */
export function safeNext(value: string | null): string {
  return value?.startsWith("/") && !value.startsWith("//") ? value : "/";
}

/**
 * With accounts: an expired session goes to the sign-in page, a disabled or deleted account to
 * the page that says so. Returns true when it took over.
 */
export function handleAccessError(error: unknown): boolean {
  if (!signsIn()) return false;
  const code = errorCode(error);
  const onAccountPage = window.location.pathname.startsWith("/account");
  if (code === "UNAUTHORIZED" && !onAccountPage) {
    window.location.assign(signInUrl());
    return true;
  }
  if ((code === "ACCOUNT_DISABLED" || code === "ACCOUNT_DELETED") && !onAccountPage) {
    window.location.assign("/account/restricted");
    return true;
  }
  return false;
}

/** "Jo Doe"; an account without a name shows its email. */
export function displayName(user: {
  firstName: string | null;
  lastName: string | null;
  email?: string | null;
}): string {
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || "";
}

/** "JD" for a picture-less avatar. */
export function initials(user: { firstName: string | null; lastName: string | null }): string {
  return [user.firstName, user.lastName]
    .map((part) => part?.trim()[0] ?? "")
    .join("")
    .toUpperCase();
}
